<script setup lang="ts">
import { useIntersectionObserver, useScroll } from '@vueuse/core';
import { computed, nextTick, ref, toRef, useTemplateRef, watch } from 'vue';
import VuePdfEmbed, { useVuePdfEmbed } from 'vue-pdf-embed';

import 'vue-pdf-embed/dist/styles/annotationLayer.css';
import 'vue-pdf-embed/dist/styles/textLayer.css';

const { source: sourceProp } = defineProps<{
	source: string;
}>();

const source = toRef(() => sourceProp);
const containerRef = useTemplateRef('container');
const pagesRef = useTemplateRef('pages');
const pageVisibility = ref<Record<number, boolean>>({});

const currentPage = ref(1);
const isFullscreen = ref(false);

const loadingProgress = ref(0);
const loadingError = ref<null | unknown>(null);
const isPasswordRequired = ref(false);
const isPasswordWrong = ref(false);
const passwordCallback = ref<null | ((password: string) => void)>(null);
const { doc } = useVuePdfEmbed({
	source,
	onProgress: (progress) => {
		loadingProgress.value = progress.loaded / progress.total;
	},
	onError: (error) => {
		loadingError.value = error;
	},
	onPasswordRequest: ({ callback, isWrongPassword }) => {
		isPasswordRequired.value = true;
		isPasswordWrong.value = isWrongPassword;

		passwordCallback.value = (password: string) => {
			callback(password);
			isPasswordRequired.value = false;
			passwordCallback.value = null;
		};
	},
});

watch(source, () => {
	loadingError.value = null;
	loadingProgress.value = 0;
	isPasswordRequired.value = false;
	isPasswordWrong.value = false;
});

const pageNums = computed(() =>
	doc.value
		? [...Array.from({ length: doc.value.numPages + 1 }).keys()].slice(1)
		: [],
);

watch(pageNums, (newPageNums) => {
	pageVisibility.value = { [newPageNums[0]!]: true };
});

let pauseScroll = false;

watch(currentPage, (newPage, oldPage) => {
	if (pauseScroll || newPage < 0 || newPage > (doc.value?.numPages ?? 0)) return;

	const el = pagesRef.value?.[newPage - 1];

	if (!el) return;

	el.scrollIntoView({
		behavior: 'smooth',
		block: oldPage < newPage ? 'start' : 'end',
	});
}, {
	flush: 'post',
});

const { directions: scrollDirections } = useScroll(containerRef);

useIntersectionObserver(() => pagesRef.value ?? [], (entries) => {
	if (!pagesRef.value) return;

	let lowestPage = 1;
	let highestPage = 0;

	for (const entry of entries) {
		if (entry.isIntersecting) {
			const index: number = pagesRef.value!.indexOf(entry.target as HTMLDivElement);
			if (index === -1) continue;

			const pageNum = pageNums.value[index];
			if (!pageNum) continue;

			if (pageNum < lowestPage) lowestPage = pageNum;
			if (pageNum > highestPage) highestPage = pageNum;
			pageVisibility.value[pageNum] = true;
		}
	}

	pauseScroll = true;

	if (scrollDirections.top) {
		currentPage.value = lowestPage;
	}

	if (highestPage !== 0 && scrollDirections.bottom) {
		currentPage.value = highestPage;
	}

	nextTick(() => {
		pauseScroll = false;
	});
});
</script>

<template>
	<div
		class="group flex h-full flex-col overflow-hidden"
		:class="{
			'relative': !isFullscreen,
			'fixed inset-0 z-1000 bg-default': isFullscreen,
		}"
		:style="{ '--toolbar-height': '3rem' }"
	>
		<PdfPreviewHeader
			v-if="doc"
			v-model:current-page="currentPage"
			v-model:fullscreen="isFullscreen"
			:source
			:doc
		/>
		<div ref="container" class="flex-1 overflow-auto" :class="{ 'group-hover:-mt-(--toolbar-height)': !!doc && !isFullscreen }">
			<FileLoadError v-if="loadingError" class="min-h-64" />
			<PdfPreviewLoading v-else-if="loadingProgress < 1" :progress="loadingProgress" />
			<PdfPreviewPasswordProtected
				v-else-if="isPasswordRequired"
				:password-wrong="isPasswordWrong"
				@unlock="(password) => passwordCallback?.(password)"
			/>
			<div v-else class="bg-muted" :class="{ 'flex flex-col items-center py-10': isFullscreen }">
				<div v-for="pageNum in pageNums" :key="pageNum" ref="pages" class="not-last:mb-8">
					<VuePdfEmbed
						v-if="pageVisibility[pageNum]"
						annotation-layer
						text-layer
						:source="doc"
						:page="pageNum"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
